/**
 * Main tap screen & producer list
 */

import { PRODUCERS, getProducerCost, MILESTONES } from '../game/data/producers.js';
import { getCurrentRank, getNextPromotableRank } from '../game/data/ranks.js';
import { getTapPower, getTotalCps, getCritChance, getCritMultiplier } from '../game/systems/production.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { dispatch, getState, showFloatText, getViewState, onRender, showToast, showAdRewardPopup, showRankPopup } from './renderer.js';
import { AdManager, getAdRewards } from '../monetization/index.js';
import { initWorkerAgents, updateWorkerAgents } from './workerAgents.js';

/** Map rank grade to character image + CSS class */
interface CharacterVisual {
  image: string;
  cssClass: string;
}

function getCharacterVisual(grade: string): CharacterVisual {
  switch (grade) {
    case 'E': case 'D': return { image: 'images/agent-rank-e.png', cssClass: 'rank-e' };
    case 'C': case 'B': return { image: 'images/agent-rank-c.png', cssClass: 'rank-c' };
    case 'A': case 'S': return { image: 'images/agent-rank-a.png', cssClass: 'rank-a' };
    case 'SS': return { image: 'images/agent-rank-ss.png', cssClass: 'rank-ss' };
    case 'SSS': return { image: 'images/agent-rank-sss.png', cssClass: 'rank-sss' };
    default: return { image: 'images/agent-rank-e.png', cssClass: 'rank-e' };
  }
}

const PRODUCER_IMAGES: Record<string, string> = {
  informant: 'images/prod-informant.png',
  hacker: 'images/prod-hacker.png',
  disguiser: 'images/prod-disguiser.png',
  mindController: 'images/prod-mindController.png',
  secretLab: 'images/prod-secretLab.png',
  mediaCell: 'images/prod-mediaCell.png',
  shadowGovt: 'images/prod-shadowGovt.png',
  deepBase: 'images/prod-deepBase.png',
  orbitalStation: 'images/prod-orbitalStation.png',
  dimensionGate: 'images/prod-dimensionGate.png',
  planetController: 'images/prod-planetController.png',
  galacticBeacon: 'images/prod-galacticBeacon.png',
};

// Track whether the main screen skeleton has been built (to avoid image flicker)
let mainScreenBuilt = false;
let lastCharImg = '';

export function initMainScreen() {
  initWorkerAgents(() => getState());
  onRender(renderMainScreen, { alwaysTabs: ['main'] });
}

function renderMainScreen() {
  if (getViewState().currentTab !== 'main') {
    mainScreenBuilt = false; // Reset when leaving tab
    producerListBuilt = false;
    lastPromoteKey = '';
    return;
  }

  const container = document.getElementById('screen-content');
  if (!container) return;

  const state = getState();
  const tapPower = getTapPower(state);
  const comboActive = state.comboCount > 0;
  const rank = getCurrentRank(state.rank ?? 'E');
  const visual = getCharacterVisual(rank.grade);
  const nextRank = getNextPromotableRank(state.rank ?? 'E', state.totalIpEarned, state.prestigeCount);

  // First render: build the skeleton once
  if (!mainScreenBuilt || !container.querySelector('.main-screen')) {
    mainScreenBuilt = true;
    lastCharImg = visual.cssClass;
    container.innerHTML = `
      <div class="main-screen">
        <div class="tap-area" id="tap-area">
          <div class="tap-character ${visual.cssClass}" id="tap-char">
            <div class="char-glow"></div>
            <img src="${visual.image}" alt="${rank.grade}" class="char-img" id="char-img" />
            <div class="char-rank-badge" id="char-rank-badge">${rank.grade}</div>
          </div>
          <div id="promote-area"></div>
          <div class="tap-power-display" id="tap-power-display">+${formatNumber(tapPower)}/tap</div>
          <div class="combo-display ${comboActive ? 'active' : ''}" id="combo-display">
            ${t('main_combo')} x${state.comboCount}
          </div>
        </div>
        <div class="main-ad-row" id="main-ad-row">
          <button class="ad-btn" id="main-ad-income">\u{1F4FA} ${t('ad_income_x5')}</button>
          <button class="ad-btn" id="main-ad-tap">\u{1F4FA} ${t('ad_tap_x10')}</button>
          <button class="ad-btn" id="main-ad-cps">\u{1F4FA} ${t('ad_cps_boost')}</button>
        </div>
        <div class="producer-list" id="producer-list"></div>
      </div>
    `;

    // Tap handler on entire tap area (bind once)
    const tapArea = document.getElementById('tap-area');
    if (tapArea) {
      tapArea.addEventListener('pointerdown', (e) => {
        const preState = getState();
        const critChance = getCritChance(preState);
        const isCrit = Math.random() < critChance;

        dispatch({ type: 'TAP' });
        playSfx('tap');
        const newState = getState();
        let power = getTapPower(newState);

        if (isCrit) {
          const critMult = getCritMultiplier(newState);
          const bonusIp = Math.floor(power * (critMult - 1));
          dispatch({ type: 'ADD_IP', payload: { amount: bonusIp } });
          power = Math.floor(power * critMult);

          // Critical UI effects
          showFloatText(
            e.clientX + (Math.random() - 0.5) * 40,
            e.clientY - 30,
            `CRIT! +${formatNumber(power)}`,
            true
          );
          if (shouldRunCritEffect()) {
            spawnCritParticles(e.clientX, e.clientY);
            critScreenShake();
            critFlash(e.clientX, e.clientY);
            critCharGlow();
            hapticFeedback();
          }
        } else {
          showFloatText(
            e.clientX + (Math.random() - 0.5) * 40,
            e.clientY - 20,
            '+' + formatNumber(power)
          );
        }
        // Impact effects
        spawnTapParticles(e.clientX, e.clientY);
        hapticFeedback();
        tapPunchAnim();
      });
    }

    // Ad handlers (bind once with skeleton)
    const adIncomeBtn = document.getElementById('main-ad-income');
    if (adIncomeBtn) {
      adIncomeBtn.addEventListener('click', () => {
        const curState = getState();
        const cps = getTotalCps(curState);
        const ipGain = cps * 1800; // 30 minutes of income
        const rewards = getAdRewards();
        showAdRewardPopup(
          t('ad_income_x5'),
          t('ad_income_x5_desc').replace('{amount}', formatNumber(ipGain)),
          () => {
            AdManager.showRewardedAd(
              rewards.INCOME_X5,
              () => {
                const freshState = getState();
                const freshCps = getTotalCps(freshState);
                const finalGain = freshCps * 1800; // 30 minutes of income
                dispatch({ type: 'AD_WATCHED', payload: { rewardType: 'income_x5' } });
                dispatch({ type: 'ADD_IP', payload: { amount: finalGain } });
                playSfx('boost');
                showToast(`+${formatNumber(finalGain)} \u{1F6F8}`);
              },
              (err) => { console.warn('[Ad]', err); }
            );
          }
        );
      });
    }

    const adTapBtn = document.getElementById('main-ad-tap');
    if (adTapBtn) {
      adTapBtn.addEventListener('click', () => {
        const rewards = getAdRewards();
        showAdRewardPopup(
          t('ad_tap_x10'),
          t('ad_tap_x10_desc'),
          () => {
            AdManager.showRewardedAd(
              rewards.TAP_X10,
              () => {
                dispatch({ type: 'AD_WATCHED', payload: { rewardType: 'tap_x10' } });
                dispatch({ type: 'ACTIVATE_BOOST', payload: { boost: { type: 'tap10x', multiplier: 10, endsAt: Date.now() + 10_000 } } });
                playSfx('boost');
                showToast(t('ad_tap_x10'));
              },
              (err) => { console.warn('[Ad]', err); }
            );
          }
        );
      });
    }

    const adCpsBtn = document.getElementById('main-ad-cps');
    if (adCpsBtn) {
      adCpsBtn.addEventListener('click', () => {
        const rewards = getAdRewards();
        showAdRewardPopup(
          t('ad_cps_boost'),
          t('ad_prestige_desc'),
          () => {
            AdManager.showRewardedAd(
              rewards.CPS_BOOST,
              () => {
                dispatch({ type: 'AD_WATCHED', payload: { rewardType: 'cps_boost' } });
                dispatch({ type: 'ACTIVATE_BOOST', payload: { boost: { type: 'cps5x', multiplier: 10, endsAt: Date.now() + 15_000 } } });
                playSfx('boost');
                showToast(t('ad_cps_boost'));
              },
              (err) => { console.warn('[Ad]', err); }
            );
          }
        );
      });
    }
  } else {
    // Subsequent renders: only update dynamic parts
    // Update character visual if rank changed
    if (visual.cssClass !== lastCharImg) {
      const charEl = document.getElementById('tap-char');
      if (charEl) {
        charEl.className = `tap-character ${visual.cssClass}`;
      }
      const imgEl = document.getElementById('char-img') as HTMLImageElement | null;
      if (imgEl) imgEl.src = visual.image;
      const badgeEl = document.getElementById('char-rank-badge');
      if (badgeEl) badgeEl.textContent = rank.grade;
      lastCharImg = visual.cssClass;
    }

    // Update tap power display
    const tpEl = document.getElementById('tap-power-display');
    if (tpEl) tpEl.textContent = `+${formatNumber(tapPower)}/tap`;

    // Update combo display
    const comboEl = document.getElementById('combo-display');
    if (comboEl) {
      comboEl.className = `combo-display ${comboActive ? 'active' : ''}`;
      comboEl.textContent = `${t('main_combo')} x${state.comboCount}`;
    }

  }

  // Promote button (runs every render)
  updatePromoteButton(state, nextRank);

  // Worker agents on tap area (cute animated producer icons)
  updateWorkerAgents();

  renderProducerList();
}

/** Track if producer list DOM has been built */
let producerListBuilt = false;

function renderProducerList() {
  const listEl = document.getElementById('producer-list');
  if (!listEl) return;

  const state = getState();
  const discountPct = state.prestigeUpgrades.agentDiscount * 5;

  // Build skeleton once, then patch
  if (!producerListBuilt || listEl.children.length === 0) {
    producerListBuilt = true;
    buildProducerListFull(listEl, state, discountPct);
    return;
  }

  // Patch existing DOM nodes
  let idx = 0;
  for (const prod of PRODUCERS) {
    const card = listEl.children[idx] as HTMLElement | undefined;
    if (!card) break;
    idx++;

    const uc = prod.unlockCondition;
    let locked = false;

    if (uc.type === 'prevProducer' && (state.producers[uc.producerId]?.count ?? 0) < 1) {
      locked = true;
    } else if (uc.type === 'prestige' && state.prestigeCount < uc.count) {
      locked = true;
    }
    // Also check previous producer in the list for prestige-gated producers
    if (!locked && uc.type === 'prestige') {
      const prodIdx = PRODUCERS.indexOf(prod);
      if (prodIdx > 0) {
        const prevProd = PRODUCERS[prodIdx - 1];
        if ((state.producers[prevProd.id]?.count ?? 0) < 1) locked = true;
      }
    }

    if (locked) {
      // If this card was previously unlocked, rebuild full list
      if (card.dataset.pid) {
        buildProducerListFull(listEl, state, discountPct);
        return;
      }
      continue;
    }

    // If this card was previously locked, rebuild full list
    if (!card.dataset.pid) {
      buildProducerListFull(listEl, state, discountPct);
      return;
    }

    const owned = state.producers[prod.id]?.count ?? 0;
    const cost = getProducerCost(prod.baseCost, owned, discountPct);
    const canAfford = state.ip >= cost;

    // Update count
    const countEl = card.querySelector('.producer-count');
    if (countEl) countEl.textContent = `x${owned}`;

    // Update CPS
    const cpsEl = card.querySelector('.producer-cps');
    if (cpsEl) cpsEl.textContent = `${formatNumber(prod.baseCps * (owned || 1))}/s`;

    // Update buy button
    const buyBtn = card.querySelector('.buy-btn') as HTMLButtonElement | null;
    if (buyBtn) {
      buyBtn.disabled = !canAfford;
      buyBtn.textContent = `\u{1F6F8} ${formatNumber(cost)}`;
    }

    // Update affordable class
    if (canAfford) {
      card.classList.add('affordable');
    } else {
      card.classList.remove('affordable');
    }
  }
}

/** Full rebuild of producer list (only on first render or unlock state change) */
function buildProducerListFull(listEl: HTMLElement, state: ReturnType<typeof getState>, discountPct: number) {
  let html = '';
  for (const prod of PRODUCERS) {
    const uc = prod.unlockCondition;
    let locked = false;
    let lockReason = '';

    if (uc.type === 'prevProducer' && (state.producers[uc.producerId]?.count ?? 0) < 1) {
      locked = true;
      const prevProd = PRODUCERS.find(p => p.id === uc.producerId);
      lockReason = t('prod_locked') + (prevProd ? t(prevProd.nameKey) : uc.producerId);
    } else if (uc.type === 'prestige' && state.prestigeCount < uc.count) {
      locked = true;
      lockReason = t('prod_locked') + t('prod_locked_prestige').replace('{n}', String(uc.count));
    }
    // Also check previous producer in the list for prestige-gated producers
    if (!locked && uc.type === 'prestige') {
      const prodIdx = PRODUCERS.indexOf(prod);
      if (prodIdx > 0) {
        const prevProd = PRODUCERS[prodIdx - 1];
        if ((state.producers[prevProd.id]?.count ?? 0) < 1) {
          locked = true;
          lockReason = t('prod_locked') + t(prevProd.nameKey);
        }
      }
    }

    if (locked) {
      html += `
        <div class="producer-card">
          <div class="producer-icon">\u{1F512}</div>
          <div class="producer-info">
            <div class="producer-locked">${lockReason}</div>
          </div>
        </div>
      `;
      continue;
    }

    const owned = state.producers[prod.id]?.count ?? 0;
    const cost = getProducerCost(prod.baseCost, owned, discountPct);
    const canAfford = state.ip >= cost;
    const imgSrc = PRODUCER_IMAGES[prod.id] || '';

    // Next milestone info
    const nextMs = MILESTONES.find(m => owned < m.count);
    const milestoneHtml = nextMs
      ? `<div class="producer-milestone">${t('milestone_next')}: ${owned}/${nextMs.count} (x${nextMs.multiplier})</div>`
      : (owned > 0 ? `<div class="producer-milestone maxed">x${MILESTONES[MILESTONES.length - 1].multiplier}</div>` : '');

    html += `
      <div class="producer-card ${canAfford ? 'affordable' : ''}" data-pid="${prod.id}">
        <div class="producer-icon"><img src="${imgSrc}" alt="" class="producer-icon-img" /></div>
        <div class="producer-info">
          <div class="producer-name">${t(prod.nameKey)}</div>
          <div class="producer-desc">${t(prod.descKey)}</div>
          <div class="producer-cps">${formatNumber(prod.baseCps * (owned || 1))}/s</div>
          ${milestoneHtml}
        </div>
        <div class="producer-right">
          <div class="producer-count-label">OWNED</div>
          <div class="producer-count">x${owned}</div>
          <button class="buy-btn" ${canAfford ? '' : 'disabled'} data-buy="${prod.id}">
            \u{1F6F8} ${formatNumber(cost)}
          </button>
        </div>
      </div>
    `;
  }

  listEl.innerHTML = html;

  // Buy handlers
  listEl.querySelectorAll('[data-buy]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const pid = (btn as HTMLElement).dataset.buy!;
      dispatch({ type: 'BUY_PRODUCER', payload: { producerId: pid } });
      playSfx('purchase');
    });
  });
}

// === PARTICLE POOL SYSTEM ===

/** Maximum particles allowed on screen simultaneously */
const MAX_ACTIVE_PARTICLES = 30;

/** Pool sizes per type */
const TAP_POOL_SIZE = 20;
const CRIT_POOL_SIZE = 15;
const FLASH_POOL_SIZE = 2;
const CRIT_EFFECT_THROTTLE_MS = 120;

/** Track total active particles across all pools */
let activeParticleCount = 0;

interface ParticlePool {
  elements: HTMLDivElement[];
  activeCount: number;
}

/** Create a pre-allocated pool of DOM elements */
function createPool(size: number): ParticlePool {
  const pool: ParticlePool = { elements: [], activeCount: 0 };
  for (let i = 0; i < size; i++) {
    const el = document.createElement('div');
    el.style.display = 'none';
    el.dataset.poolActive = '0';
    document.body.appendChild(el);
    el.addEventListener('animationend', () => {
      if (el.dataset.poolActive === '1') {
        el.style.display = 'none';
        el.dataset.poolActive = '0';
        pool.activeCount = Math.max(0, pool.activeCount - 1);
        activeParticleCount = Math.max(0, activeParticleCount - 1);
      }
    });
    pool.elements.push(el);
  }
  return pool;
}

/** Get an inactive element from the pool, or null if all are in use */
function acquireFromPool(pool: ParticlePool, className: string): HTMLDivElement | null {
  if (activeParticleCount >= MAX_ACTIVE_PARTICLES) return null;
  for (const el of pool.elements) {
    if (el.dataset.poolActive === '0') {
      el.dataset.poolActive = '1';
      el.className = className;
      pool.activeCount++;
      activeParticleCount++;
      return el;
    }
  }
  return null;
}

// Lazily initialized pools (created on first use after DOM is ready)
let tapPool: ParticlePool | null = null;
let critPool: ParticlePool | null = null;
let flashPool: ParticlePool | null = null;
let lastCritEffectAt = 0;

function shouldRunCritEffect(now = performance.now()): boolean {
  if (now - lastCritEffectAt < CRIT_EFFECT_THROTTLE_MS) return false;
  lastCritEffectAt = now;
  return true;
}

function getTapPool(): ParticlePool {
  if (!tapPool) tapPool = createPool(TAP_POOL_SIZE);
  return tapPool;
}
function getCritPool(): ParticlePool {
  if (!critPool) critPool = createPool(CRIT_POOL_SIZE);
  return critPool;
}
function getFlashPool(): ParticlePool {
  if (!flashPool) flashPool = createPool(FLASH_POOL_SIZE);
  return flashPool;
}

// === TAP IMPACT EFFECTS ===

/** Spawn burst particles at tap position (pooled, reduced count) */
function spawnTapParticles(x: number, y: number) {
  const pool = getTapPool();
  const count = 3 + Math.floor(Math.random() * 3); // 3-5 particles (was 6-9)
  for (let i = 0; i < count; i++) {
    const particle = acquireFromPool(pool, 'tap-particle');
    if (!particle) break; // Pool exhausted or cap reached
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
    const dist = 30 + Math.random() * 50;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const size = 3 + Math.random() * 5;
    const s = particle.style;
    s.display = '';
    s.left = `${x}px`;
    s.top = `${y}px`;
    s.width = `${size}px`;
    s.height = `${size}px`;
    s.setProperty('--dx', `${dx}px`);
    s.setProperty('--dy', `${dy}px`);
  }
}


/** Haptic feedback for mobile - only after user gesture */
let userHasInteracted = false;
document.addEventListener('pointerdown', () => { userHasInteracted = true; }, { once: true });

function hapticFeedback() {
  if (userHasInteracted && navigator.vibrate) {
    try { navigator.vibrate(15); } catch { /* ignore */ }
  }
}

/** Spawn golden burst particles for critical hits (pooled, reduced count) */
function spawnCritParticles(x: number, y: number) {
  const pool = getCritPool();
  const count = 5 + Math.floor(Math.random() * 4); // 5-8 particles (was 10-15)
  for (let i = 0; i < count; i++) {
    const particle = acquireFromPool(pool, 'crit-particle');
    if (!particle) break; // Pool exhausted or cap reached
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
    const dist = 50 + Math.random() * 80;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const size = 4 + Math.random() * 8;
    const s = particle.style;
    s.display = '';
    s.left = `${x}px`;
    s.top = `${y}px`;
    s.width = `${size}px`;
    s.height = `${size}px`;
    s.setProperty('--dx', `${dx}px`);
    s.setProperty('--dy', `${dy}px`);
  }
}

/** Screen shake for critical hits (no reflow) */
function critScreenShake() {
  const container = document.getElementById('screen-content');
  if (container) {
    container.classList.remove('crit-shake');
    // Use double-rAF to restart animation without forced reflow
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.classList.add('crit-shake');
      });
    });
  }
}

/** Red flash overlay for critical hits (pooled) */
function critFlash(x: number, y: number) {
  const pool = getFlashPool();
  const flash = acquireFromPool(pool, 'crit-flash');
  if (!flash) return; // Pool exhausted or cap reached
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  flash.style.setProperty('--cx', `${(x / vw * 100).toFixed(1)}%`);
  flash.style.setProperty('--cy', `${(y / vh * 100).toFixed(1)}%`);
}

/** Glow effect on character for critical hits */
function critCharGlow() {
  const charEl = document.getElementById('tap-char');
  if (!charEl) return;
  charEl.classList.add('crit-glow');
  setTimeout(() => charEl.classList.remove('crit-glow'), 400);
}

/** Character punch + ring pulse animation (no reflow) */
function tapPunchAnim() {
  const charEl = document.getElementById('tap-char');
  const tapArea = document.getElementById('tap-area');
  if (charEl) {
    charEl.classList.remove('punched');
    // Use double-rAF to restart animation without forced reflow
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (charEl) charEl.classList.add('punched');
      });
    });
  }
  if (tapArea) {
    tapArea.classList.remove('ring-pulse');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (tapArea) tapArea.classList.add('ring-pulse');
      });
    });
  }
}

// === RANK PROMOTION ===

import type { RankDef } from '../game/data/ranks.js';

/** Cache key to avoid re-rendering promote button every frame */
let lastPromoteKey = '';

function updatePromoteButton(state: ReturnType<typeof getState>, nextRank: RankDef | null) {
  const area = document.getElementById('promote-area');
  if (!area) return;

  if (!nextRank) {
    if (lastPromoteKey !== 'none') {
      lastPromoteKey = 'none';
      area.innerHTML = '';
    }
    return;
  }

  const canAfford = state.ip >= nextRank.promotionCost;
  const key = `${nextRank.grade}:${canAfford}`;
  if (key === lastPromoteKey) return;
  lastPromoteKey = key;

  const html = `<button class="promote-btn ${canAfford ? 'affordable' : ''}" id="promote-btn" ${canAfford ? '' : 'disabled'}>
    <span class="promote-label">\u2B06 ${t('rank_promote')} ${nextRank.grade}</span>
    <span class="promote-cost">\u{1F6F8} ${formatNumber(nextRank.promotionCost)}</span>
    <span class="promote-reward">TAP x${nextRank.tapMultiplier}</span>
  </button>`;

  area.innerHTML = html;
  const btn = document.getElementById('promote-btn');
  if (btn && canAfford) {
    btn.addEventListener('click', () => {
      const prevRankDef = getCurrentRank(state.rank ?? 'E');
      dispatch({ type: 'PROMOTE_RANK' });
      playSfx('prestige');
      showRankPopup(
        prevRankDef.grade, t(prevRankDef.nameKey), prevRankDef.tapMultiplier,
        nextRank.grade, t(nextRank.nameKey), nextRank.tapMultiplier,
      );
      // Force rebuild to update character visual
      mainScreenBuilt = false;
      lastPromoteKey = '';
    });
  }
}
